import type { SetupDraft, SetupDraftChange } from '@deskworks/shared-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, Check, ChevronDown, ChevronRight, CircleDot, FileDiff, GraduationCap, Info, Rocket, Undo2 } from 'lucide-react';
import { useState } from 'react';
import { useStartTraining } from '@/components/desk/desk-training';
import { RelativeTime } from '@/components/relative-time';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { api } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { t, tr } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { useRealtimeEnvelope } from '@/lib/realtime';
import { cn } from '@/lib/utils';

const KIND_LABEL: Record<SetupDraftChange['kind'], string> = {
  create_agent: t('New agent'),
  update_agent: t('Agent'),
  create_desk: t('New desk'),
  update_desk: t('Desk settings'),
  set_desk_goals: t('Goals'),
  update_playbook: t('Playbook'),
  add_desk_agent: t('Desk team'),
  attach_desk_connection: t('Connection'),
  create_trigger: t('Trigger'),
  create_work_item: t('Work Item'),
  create_work_item_type: t('New Work Item type'),
  attach_resource: t('File for a desk'),
  create_skill: t('New skill'),
  update_skill: t('Skill'),
  attach_skill: t('Skill for an agent'),
  run_desk: t('First run'),
  create_table: t('New table'),
  create_view: t('New view'),
  share_connection: t('Share a connection'),
};

const FIELD_LABEL: Record<string, string> = {
  name: t('Name'),
  description: t('Description'),
  instructions: t('Instructions'),
  model: t('Model'),
  assignment: t('Assignment'),
  agent_mode: t('Agent mode'),
  work_item_approval: t('Approval of new work'),
  max_parallel_work_items: t('Work Items at a time'),
  playbook: t('Playbook'),
  goals: t('Goals'),
  role: t('Role'),
  permissions: t('Permissions'),
  type: t('Type'),
  cron: t('Schedule (cron)'),
  timezone: t('Time zone'),
  title: t('Title'),
  goal: t('Done when'),
  approval_rule: t('Ask before'),
  reason: t('Reason'),
  capabilities: t('Capabilities'),
  purpose: t('Purpose'),
  columns: t('Columns'),
  prompt: t('What it shows'),
};
/** References and refs: already part of the summary line. */
const HIDDEN_FIELDS = new Set(['ref', 'desk', 'agent', 'lead_agent', 'assigned_agent', 'connection', 'skill', 'runtime_profile_id', 'share_with']);

function text(value: unknown): string {
  if (value == null) return '';
  if (Array.isArray(value)) {
    return value
      .map((v) => {
        if (v && typeof v === 'object' && 'title' in v) return `• ${(v as { title: string }).title}`;
        if (v && typeof v === 'object' && 'type' in v) {
          const c = v as { name: string; type: string; required?: boolean; default?: string; primary_key?: boolean };
          return `• ${c.name} ${c.type}${c.primary_key ? `, ${t('primary key')}` : c.required ? `, ${t('required')}` : ''}${c.default ? `, ${t('default {value}', { value: c.default })}` : ''}`;
        }
        return String(v);
      })
      .join('\n');
  }
  return typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
}

/** Line diff for playbooks and goals: removed lines red, added green, unchanged plain. */
function LineDiff({ before, after }: { before: string; after: string }) {
  const old = before.split('\n');
  const now = after.split('\n');
  const removed = old.filter((l) => !now.includes(l) && l.trim());
  return (
    <div className="rounded-md border bg-muted/30 p-2 font-mono text-[11.5px] leading-relaxed whitespace-pre-wrap">
      {removed.map((l, i) => (
        <div key={`r${i}`} className="text-red-700 line-through decoration-red-400/60 dark:text-red-400">
          − {l}
        </div>
      ))}
      {now.map((l, i) => (
        <div key={`a${i}`} className={old.includes(l) ? 'text-muted-foreground' : 'text-emerald-700 dark:text-emerald-400'}>
          {old.includes(l) ? '  ' : '+ '}
          {l}
        </div>
      ))}
    </div>
  );
}

function ChangeDetails({ change }: { change: SetupDraftChange }) {
  const fields = Object.entries(change.input).filter(([k, v]) => !HIDDEN_FIELDS.has(k) && v !== undefined && v !== '');
  if (!fields.length) return null;
  return (
    <div className="mt-2 space-y-2 text-xs">
      {fields.map(([key, value]) => {
        const before = change.before && key in change.before ? text(change.before[key]) : null;
        const after = text(value);
        return (
          <div key={key}>
            <div className="mb-0.5 font-medium text-muted-foreground">{FIELD_LABEL[key] ?? key}</div>
            {before !== null && (key === 'playbook' || key === 'goals' || after.includes('\n') || before.includes('\n')) ? (
              <LineDiff before={before} after={after} />
            ) : before !== null ? (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-red-700 line-through dark:text-red-400">{before || t('(empty)')}</span>
                <span className="text-muted-foreground">→</span>
                <span className="text-emerald-700 dark:text-emerald-400">{after}</span>
              </div>
            ) : (
              <div className="whitespace-pre-wrap text-foreground/90">{after}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ChangeRow({ change, draft }: { change: SetupDraftChange; draft: SetupDraft }) {
  const [open, setOpen] = useState(false);
  // Links go to the workspace the change was for (docs/features/setup-assistant.md, "Across the account").
  const workspaceId = change.workspaceId;
  const editable = draft.status === 'open';
  const toggle = useApiMutation((enabled: boolean) => api.setup.toggle(draft.id, change.number, enabled), {
    invalidate: [qk.setupDraft(draft.conversationId)],
  });
  const created = change.status === 'applied' && change.kind === 'create_desk' && change.resultId;
  const createdView = change.status === 'applied' && change.kind === 'create_view' && change.resultId;
  return (
    <li className={cn('px-4 py-3', !change.enabled && 'opacity-55')}>
      <div className="flex items-start gap-3">
        {editable ? (
          <Checkbox
            className="mt-0.5"
            checked={change.enabled}
            disabled={toggle.isPending}
            onChange={(e) => toggle.mutate(e.target.checked)}
            aria-label={change.enabled ? t('Switch this change off') : t('Switch this change on')}
          />
        ) : change.status === 'applied' ? (
          <Check className="mt-0.5 size-4 shrink-0 text-emerald-600" />
        ) : (
          <CircleDot className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        )}
        <div className="min-w-0 flex-1">
          <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-start gap-1.5 text-left">
            <span className="min-w-0 flex-1">
              <span className="mr-1.5 text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
                #{change.number} {KIND_LABEL[change.kind] ?? change.kind}
              </span>
              <span className="text-sm">{change.summary}</span>
            </span>
            {open ? <ChevronDown className="mt-0.5 size-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="mt-0.5 size-4 shrink-0 text-muted-foreground" />}
          </button>
          {change.error && (
            <div className="mt-1.5 flex items-start gap-1.5 text-xs text-red-700 dark:text-red-400">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0" /> {change.error}
            </div>
          )}
          {created && (
            <Link to="/w/$workspaceId/desks/$deskId" params={{ workspaceId, deskId: change.resultId! }} className="mt-1 inline-block text-xs text-primary hover:underline">
              {t('Open the desk')}
            </Link>
          )}
          {createdView && (
            <Link to="/w/$workspaceId/views/$viewId" params={{ workspaceId, viewId: change.resultId! }} className="mt-1 inline-block text-xs text-primary hover:underline">
              {t('Open the view')}
            </Link>
          )}
          {change.status === 'applied' && change.kind === 'create_table' && (
            <Link to="/w/$workspaceId/database" params={{ workspaceId }} className="mt-1 inline-block text-xs text-primary hover:underline">
              {t('Open the database')}
            </Link>
          )}
          {open && <ChangeDetails change={change} />}
        </div>
      </div>
    </li>
  );
}

/**
 * After Apply, the desks the draft created are set up but know nothing yet: their first training is the next step
 * (docs/features/desk-training.md).
 */
function TrainNewDesks({ draft }: { draft: SetupDraft }) {
  const desks = draft.changes.filter((c) => c.kind === 'create_desk' && c.status === 'applied' && c.resultId);
  if (!desks.length) return null;
  return (
    <div className="border-t bg-sky-50/60 px-4 py-3 dark:bg-sky-950/30">
      <p className="flex items-center gap-1.5 text-sm font-medium">
        <GraduationCap className="size-4 text-sky-700 dark:text-sky-300" /> {t('Next: teach the desk its work')}
      </p>
      <p className="mt-0.5 text-xs text-muted-foreground">
        {t('In its first training the Lead takes real cases with you, you show the way, and it writes down how the work is done.')}
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        {desks.map((c) => (
          <StartFirstTraining key={c.id} deskId={c.resultId!} workspaceId={c.workspaceId} name={typeof c.input.name === 'string' ? c.input.name : null} />
        ))}
      </div>
    </div>
  );
}

function StartFirstTraining({ deskId, workspaceId, name }: { deskId: string; workspaceId: string; name: string | null }) {
  const start = useStartTraining({ id: deskId, workspaceId });
  return (
    <Button size="sm" onClick={() => start.mutate()} loading={start.isPending}>
      <Rocket /> {name ? t('Start the first training: {desk}', { desk: name }) : t('Start the first training')}
    </Button>
  );
}

/** The draft's changes by the workspace they change, in the order the workspaces first come up. */
function workspaceGroups(changes: SetupDraftChange[]) {
  const groups: { workspaceId: string; workspaceName: string; changes: SetupDraftChange[] }[] = [];
  for (const c of changes) {
    const group = groups.find((g) => g.workspaceId === c.workspaceId);
    if (group) group.changes.push(c);
    else groups.push({ workspaceId: c.workspaceId, workspaceName: c.workspaceName, changes: [c] });
  }
  return groups;
}

/**
 * The setup chat's draft (docs/features/setup-assistant.md): every change the
 * assistant proposes. Nothing changes until the person clicks Apply.
 */
export function DraftPanel({ conversationId, workspaceId, className }: { conversationId: string; workspaceId: string; className?: string }) {
  const queryClient = useQueryClient();
  const draft = useQuery({ queryKey: qk.setupDraft(conversationId), queryFn: () => api.setup.draft(conversationId) });
  useRealtimeEnvelope((env) => {
    const data = (env.data ?? {}) as { conversationId?: string; setupDraftId?: string };
    if (env.event === 'conversation.updated' && data.conversationId === conversationId && data.setupDraftId) {
      void queryClient.invalidateQueries({ queryKey: qk.setupDraft(conversationId) });
    }
  });
  const d = draft.data?.draft ?? null;
  // Every workspace the draft changes refreshes after Apply.
  const touched = [...new Set([workspaceId, ...(d?.changes ?? []).map((c) => c.workspaceId)])];
  const groups = workspaceGroups(d?.changes ?? []);
  const refreshWorkspace = [
    qk.setupDraft(conversationId),
    qk.conversation(conversationId),
    ['desk'],
    ...touched.flatMap((ws) => [qk.desks(ws), qk.agents(ws), qk.connections(ws), ['ws', ws, 'database'], ['ws', ws, 'views']]),
  ];
  const apply = useApiMutation(() => api.setup.apply(d!.id), { invalidate: refreshWorkspace, success: t('Draft applied') });
  const discard = useApiMutation(() => api.setup.discard(d!.id), { invalidate: [qk.setupDraft(conversationId), qk.conversation(conversationId)] });
  // A refused apply marks the change with the reason.
  const refetchOnError = () => void queryClient.invalidateQueries({ queryKey: qk.setupDraft(conversationId) });

  const enabled = d?.changes.filter((c) => c.enabled).length ?? 0;
  return (
    <aside className={cn('flex min-h-0 flex-col border-l bg-muted/20', className)}>
      <div className="flex items-center justify-between gap-2 border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <FileDiff className="size-4 text-primary" />
          <h2 className="text-sm font-semibold">{d ? t('Draft #{n}', { n: d.number }) : t('Draft')}</h2>
          {d && (
            <Badge tone={d.status === 'applied' ? 'green' : d.status === 'discarded' ? 'muted' : 'accent'}>
              {d.status === 'open' ? t('{n} of {total} on', { n: enabled, total: d.changes.length }) : d.status === 'applied' ? t('applied') : d.status === 'discarded' ? t('discarded') : d.status}
            </Badge>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {!d || (d.status === 'open' && !d.changes.length && !d.notes.length) ? (
          <div className="flex items-start gap-2 px-4 py-6 text-sm text-muted-foreground">
            <Info className="mt-0.5 size-4 shrink-0" />
            <span>{t('The assistant writes every change it proposes here. Nothing changes in the workspace until you click Apply.')}</span>
          </div>
        ) : (
          <>
            {d.notes.length > 0 && (
              <div className="m-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                <div className="mb-1 font-medium">{t('Open points')}</div>
                <ul className="list-disc space-y-0.5 pl-4">
                  {d.notes.map((n, i) => (
                    <li key={i}>{n}</li>
                  ))}
                </ul>
              </div>
            )}
            {groups.map((g) => (
              <section key={g.workspaceId}>
                {groups.length > 1 && (
                  <h3 className="border-b bg-muted/40 px-4 py-1.5 text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
                    {t('In {workspace}', { workspace: g.workspaceName })}
                  </h3>
                )}
                <ul className="divide-y">
                  {g.changes.map((c) => (
                    <ChangeRow key={c.id} change={c} draft={d} />
                  ))}
                </ul>
              </section>
            ))}
          </>
        )}
      </div>

      {d && d.status === 'open' && d.changes.length > 0 && (
        <div className="flex items-center gap-2 border-t bg-background px-4 py-3">
          <Button className="flex-1" disabled={!enabled} loading={apply.isPending} onClick={() => apply.mutate(undefined, { onError: refetchOnError })}>
            <Check /> {enabled === 1 ? t('Apply 1 change') : t('Apply {n} changes', { n: enabled })}
          </Button>
          <Button variant="ghost" loading={discard.isPending} onClick={() => discard.mutate()} title={t('Throw this draft away; nothing changes.')}>
            <Undo2 /> {t('Discard')}
          </Button>
        </div>
      )}
      {d && d.status === 'applied' && <TrainNewDesks draft={d} />}
      {d && d.status === 'applied' && (
        <div className="border-t bg-background px-4 py-3 text-xs text-muted-foreground">
          {d.appliedByName
            ? tr('Applied {time} by {name}. Tell the assistant what to change next — it starts a new draft.', {
                time: <RelativeTime value={d.appliedAt} />,
                name: d.appliedByName,
              })
            : tr('Applied {time}. Tell the assistant what to change next — it starts a new draft.', { time: <RelativeTime value={d.appliedAt} /> })}
        </div>
      )}
    </aside>
  );
}
